/**
 * Favorites Hook
 * Manages user favorites for all entity types using React Query
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import {
  getDatabaseFavorites,
  getLocalFavorites,
  toggleFavorite,
  syncLocalFavoritesToDatabase,
  getFavoritesCount,
  EntityType
} from '@/services/favoritesService';

// Query keys
export const favoritesKeys = {
  all: ['favorites'] as const,
  lists: () => [...favoritesKeys.all, 'list'] as const,
  list: (type: EntityType) => [...favoritesKeys.lists(), type] as const,
  counts: () => [...favoritesKeys.all, 'count'] as const,
  count: (type: EntityType) => [...favoritesKeys.counts(), type] as const,
  details: () => [...favoritesKeys.all, 'detail'] as const,
  detail: (type: EntityType, id: string) => [...favoritesKeys.details(), type, id] as const,
};

/**
 * Hook to get a list of favorites for a specific type
 */
export function useFavorites(type: EntityType = 'pet') {
  const { user } = useAuth();

  return useQuery({
    queryKey: favoritesKeys.list(type),
    queryFn: async () => {
      if (user?.id) {
        return await getDatabaseFavorites(user.id, type);
      } else {
        return getLocalFavorites(type);
      }
    },
    // Refresh when user changes or window focus
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to check if a specific item is favorited
 */
export function useIsFavorited(entityId: string, type: EntityType = 'pet') {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: favoritesKeys.detail(type, entityId),
    queryFn: async () => {
      if (user?.id) {
        const list = await getDatabaseFavorites(user.id, type);
        return list.includes(entityId);
      } else {
        const list = getLocalFavorites(type);
        return list.includes(entityId);
      }
    },
    initialData: () => {
      // Try to get from list cache if available
      const list = queryClient.getQueryData<string[]>(favoritesKeys.list(type));
      if (list) {
        return list.includes(entityId);
      }
      return undefined;
    },
  });
}

/**
 * Hook to toggle a favorite
 */
export function useToggleFavorite(type: EntityType = 'pet') {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entityId: string) => {
      return await toggleFavorite(type, entityId, user?.id);
    },
    onMutate: async (entityId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: favoritesKeys.list(type) });
      await queryClient.cancelQueries({ queryKey: favoritesKeys.detail(type, entityId) });
      await queryClient.cancelQueries({ queryKey: favoritesKeys.count(type) });

      // Snapshot previous value
      const previousList = queryClient.getQueryData<string[]>(favoritesKeys.list(type));
      const previousDetail = queryClient.getQueryData<boolean>(favoritesKeys.detail(type, entityId));

      // Optimistic update for list
      if (previousList) {
        const isFav = previousList.includes(entityId);
        queryClient.setQueryData<string[]>(favoritesKeys.list(type), (old) => {
          if (!old) return isFav ? [] : [entityId];
          return isFav ? old.filter(id => id !== entityId) : [...old, entityId];
        });
      }

      // Optimistic update for detail
      queryClient.setQueryData<boolean>(favoritesKeys.detail(type, entityId), (old) => !old);

      return { previousList, previousDetail };
    },
    onError: (err, entityId, context) => {
      // Rollback
      if (context?.previousList) {
        queryClient.setQueryData(favoritesKeys.list(type), context.previousList);
      }
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(favoritesKeys.detail(type, entityId), context.previousDetail);
      }

      toast({
        title: "Error",
        description: "Failed to update favorites. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: (data, entityId) => {
      // Invalidate queries to ensure sync
      queryClient.invalidateQueries({ queryKey: favoritesKeys.list(type) });
      queryClient.invalidateQueries({ queryKey: favoritesKeys.detail(type, entityId) });
      queryClient.invalidateQueries({ queryKey: favoritesKeys.count(type) });

      toast({
        title: data.isFavorited ? "Added to Favorites" : "Removed from Favorites",
        description: data.isFavorited
          ? `Saved to your ${type} favorites`
          : `Removed from your ${type} favorites`,
      });
    },
  });
}

/**
 * Hook to get favorites count
 */
export function useFavoritesCount(type: EntityType = 'pet') {
  const { user } = useAuth();

  return useQuery({
    queryKey: favoritesKeys.count(type),
    queryFn: async () => {
      return await getFavoritesCount(type, user?.id);
    },
  });
}

/**
 * Hook to sync favorites when user logs in
 */
export function useSyncFavorites() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user?.id) {
      syncLocalFavoritesToDatabase(user.id).then(() => {
        // Invalidate all favorite queries after sync
        queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      });
    }
  }, [user, queryClient]);
}
